import { useMemo, useState } from "react";
import AadhaarKpiSchoolsModal from "@/components/AadhaarKpiSchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";
import {
  AlertTriangle,
  Building2,
  Clock,
  FileText,
  User,
} from "lucide-react";
import { BlockLink, SchoolLink } from "@/components/DrilldownLink";
import {
  buildAccountabilityCards,
  buildControlPriorities,
  buildMismatchRiskBars,
  buildMbuAgeBars,
  buildOperationalSchoolRows,
  buildPrimaryOperationalAction,
  buildStackChartRows,
  buildWorkQueue,
  deriveRiskLoad,
  formatCompact,
  pct,
} from "@/utils/aadhaarDerived";
import "./AadhaarOperationalTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const PRIORITY_ICONS = {
  failed: AlertTriangle,
  "not-provided": FileText,
  mismatch: User,
  mbu: Clock,
};

const ACCOUNTABILITY_ICONS = {
  red: AlertTriangle,
  orange: Building2,
  purple: Clock,
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function AadhaarOperationalTab({
  overview = {},
  blockData = [],
  mbuData = [],
  highRiskSchools = [],
  scopeLabel = "Pune District",
}) {
  const [kpiMetricModal, setKpiMetricModal] = useState(null);
  const riskLoad = useMemo(() => deriveRiskLoad(overview), [overview]);
  const primaryAction = useMemo(() => buildPrimaryOperationalAction(blockData), [blockData]);
  const priorities = useMemo(() => buildControlPriorities(overview, blockData), [overview, blockData]);
  const stackRows = useMemo(() => buildStackChartRows(blockData, 10), [blockData]);
  const workQueue = useMemo(
    () => buildWorkQueue(overview, blockData, highRiskSchools),
    [overview, blockData, highRiskSchools]
  );
  const mismatchBars = useMemo(() => buildMismatchRiskBars(blockData, 8), [blockData]);
  const mbuBars = useMemo(() => buildMbuAgeBars(mbuData, 8), [mbuData]);
  const schoolRows = useMemo(() => buildOperationalSchoolRows(highRiskSchools, 20), [highRiskSchools]);
  const accountabilityCards = useMemo(() => buildAccountabilityCards(blockData), [blockData]);

  const openIssues =
    (overview.aadhaar_failed ?? 0) +
    (overview.aadhaar_not_provided ?? 0) +
    (overview.aadhaar_pending ?? 0);

  const highRiskCount = overview.high_risk_schools ?? highRiskSchools.length;

  const gaugeStyle = {
    background: `conic-gradient(var(--aa-danger) ${riskLoad}%, rgba(37, 99, 235, 0.08) 0)`,
  };

  const openKpiModal = (metric) => () => setKpiMetricModal(metric);
  const onKpiKeyDown = (metric) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setKpiMetricModal(metric);
    }
  };

  return (
    <div className="aa-ops-tab">
      <section className="aa-ops-hero" id="aa-ops-control">
        <div className="aa-ops-hero-layout">
          <div>
            <div className="aa-ops-main">
              <div
                className="aa-ops-gauge"
                style={gaugeStyle}
                aria-label={`Operational risk index ${riskLoad} percent`}
              >
                <div className="aa-ops-gauge-inner">
                  <strong>{riskLoad}%</strong>
                  <span>Risk Load</span>
                </div>
              </div>
              <div className="aa-ops-hero-copy">
                <h2>Exception Control Room</h2>
                <p>
                  High exception concentration is visible in a few blocks and schools. Failed and
                  not-provided records should be resolved before MBU clean-up to reduce downstream
                  verification friction.
                </p>
                <div className="aa-ops-status-row">
                  <HeroStatusChip
                    chipClass="aa-ops-status-chip"
                    onClick={openIssues > 0 ? openKpiModal("open_aadhaar_issues") : undefined}
                    ariaLabel={openIssues > 0 ? "High risk active — view schools with open Aadhaar issues" : undefined}
                  >
                    High Risk Active
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="aa-ops-status-chip"
                    variant="amber"
                    onClick={highRiskCount > 0 ? openKpiModal("high_risk_schools") : undefined}
                    ariaLabel={
                      highRiskCount > 0
                        ? `${highRiskCount} schools need action — view high-risk school list`
                        : undefined
                    }
                  >
                    {highRiskCount} Schools Need Action
                  </HeroStatusChip>
                  <HeroStatusChip chipClass="aa-ops-status-chip" variant="blue" showChevron={false}>
                    SLA View Enabled
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="aa-ops-summary-note">
              <div className="aa-ops-icon-box red">
                <AlertTriangle size={22} />
              </div>
              <div>
                <b>Primary operational action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="aa-ops-metric-grid" aria-label="Operational KPIs">
            <article
              className="aa-ops-metric-card danger clickable"
              role="button"
              tabIndex={0}
              aria-label="Open Aadhaar issues: view schools with exception backlog"
              onClick={openKpiModal("open_aadhaar_issues")}
              onKeyDown={onKpiKeyDown("open_aadhaar_issues")}
            >
              <small>Open Aadhaar Issues</small>
              <strong>{formatCompact(openIssues)}</strong>
              <span>Failed + not provided + pending</span>
            </article>
            <article
              className="aa-ops-metric-card danger clickable"
              role="button"
              tabIndex={0}
              aria-label="Failed records: view schools requiring immediate correction"
              onClick={openKpiModal("failed_records")}
              onKeyDown={onKpiKeyDown("failed_records")}
            >
              <small>Failed Records</small>
              <strong>{formatCompact(overview.aadhaar_failed ?? 0)}</strong>
              <span>Immediate correction needed</span>
            </article>
            <article className="aa-ops-metric-card">
              <small>Not Provided</small>
              <strong>{formatCompact(overview.aadhaar_not_provided ?? 0)}</strong>
              <span>Largest exception bucket</span>
            </article>
            <article
              className="aa-ops-metric-card warning clickable"
              role="button"
              tabIndex={0}
              aria-label="Name mismatch: view schools with identity verification gaps"
              onClick={openKpiModal("name_mismatch")}
              onKeyDown={onKpiKeyDown("name_mismatch")}
            >
              <small>Name Mismatch</small>
              <strong>{pct(overview.name_mismatch_pct ?? 0)}</strong>
              <span>Verification risk by block</span>
            </article>
            <article
              className="aa-ops-metric-card purple clickable"
              role="button"
              tabIndex={0}
              aria-label="MBU pending: view schools with biometric update backlog"
              onClick={openKpiModal("mbu_pending")}
              onKeyDown={onKpiKeyDown("mbu_pending")}
            >
              <small>MBU Pending</small>
              <strong>{pct(overview.mbu_pending_total_pct ?? 0)}</strong>
              <span>Age-wise follow-up required</span>
            </article>
            <article
              className="aa-ops-metric-card danger clickable"
              role="button"
              tabIndex={0}
              aria-label="High-risk schools: view schools above exception rate threshold"
              onClick={openKpiModal("high_risk_schools")}
              onKeyDown={onKpiKeyDown("high_risk_schools")}
            >
              <small>High-risk Schools</small>
              <strong>{highRiskCount}</strong>
              <span>Exception rate above threshold</span>
            </article>
          </div>
        </div>
      </section>

      <div className="aa-ops-section-title">
        <div>
          <h2>Control Priorities</h2>
          <p>
            Instead of starting with dense charts, the tab now begins with four operational priority
            cards that explain what needs attention, why it matters and who should act.
          </p>
        </div>
        <button type="button" className="aa-ops-section-action" onClick={() => scrollToId("aa-ops-schools")}>
          Review high-risk schools →
        </button>
      </div>

      <section className="aa-ops-control-grid">
        {priorities.map((card) => {
          const Icon = PRIORITY_ICONS[card.id] || FileText;
          return (
            <article key={card.id} className="aa-ops-control-card">
              <div className="aa-ops-control-head">
                <div className={`aa-ops-icon-box ${card.iconClass || ""}`.trim()}>
                  <Icon size={22} />
                </div>
                <span className={`aa-ops-status-dot ${card.dotClass || ""}`.trim()} />
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <div className="aa-ops-control-score">
                <strong>{card.value}</strong>
                <span>{card.unit}</span>
              </div>
              <div className={`aa-ops-progress-line ${card.barClass || ""}`.trim()}>
                <i style={{ width: `${card.barWidth}%` }} />
              </div>
              <div className="aa-ops-control-meta">
                <span>{card.metaLeft}</span>
                <span>Priority {card.priority}</span>
              </div>
            </article>
          );
        })}
      </section>

      <div className="aa-ops-section-title" id="aa-ops-diagnostics">
        <div>
          <h2>Block Diagnostics</h2>
          <p>
            Charts are split into clear diagnostic panels with legends, labels and action-oriented
            summaries to avoid the earlier compressed stacked view.
          </p>
        </div>
        <button type="button" className="aa-ops-section-action" onClick={() => scrollToId("aa-ops-queue")}>
          Go to action queue →
        </button>
      </div>

      <section className="aa-ops-analytics-grid">
        <article className="aa-ops-panel">
          <div className="aa-ops-panel-head">
            <div>
              <h3>Aadhaar Issues by Block</h3>
              <p>
                Stacked issue load showing failed, not provided and pending buckets. The top blocks
                should be reviewed first.
              </p>
            </div>
            <span className="aa-ops-pill red">Exception Load</span>
          </div>
          <div className="aa-ops-stack-chart" aria-label="Aadhaar issues by block stacked bar chart">
            {stackRows.map((row) => (
              <div key={row.block} className="aa-ops-stack-row">
                <div className="aa-ops-stack-label">{row.block}</div>
                <div className="aa-ops-stack-bar">
                  <span className="aa-ops-seg failed" style={{ width: `${row.failedPct}%` }} />
                  <span className="aa-ops-seg pending" style={{ width: `${row.pendingPct}%` }} />
                  <span className="aa-ops-seg notprovided" style={{ width: `${row.notProvidedPct}%` }} />
                </div>
                <div className="aa-ops-stack-value">{row.total.toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
          <div className="aa-ops-legend-row">
            <span>
              <i className="aa-ops-legend-dot" style={{ background: "var(--aa-danger)" }} />
              Failed
            </span>
            <span>
              <i className="aa-ops-legend-dot" style={{ background: "#6b7280" }} />
              Not Provided
            </span>
            <span>
              <i className="aa-ops-legend-dot" style={{ background: "var(--aa-warning)" }} />
              Pending
            </span>
          </div>
        </article>

        <article className="aa-ops-panel" id="aa-ops-queue">
          <div className="aa-ops-panel-head">
            <div>
              <h3>Officer Work Queue</h3>
              <p>
                Operational queue converted into specific work packets with business meaning and
                follow-up owner type.
              </p>
            </div>
            <span className="aa-ops-pill amber">Today</span>
          </div>
          <div className="aa-ops-work-list">
            {workQueue.map((item) => (
              <div key={item.title} className="aa-ops-work-item">
                <div className={`aa-ops-work-icon ${item.tone}`}>{item.priority}</div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.body}</p>
                </div>
                <div className="aa-ops-work-value">
                  {item.value}
                  <span>{item.owner}</span>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="aa-ops-two-column">
        <article className="aa-ops-panel">
          <div className="aa-ops-panel-head">
            <div>
              <h3>Name Mismatch Risk by Block</h3>
              <p>
                Risk ladder highlights where identity verification and spelling clean-up need
                priority attention.
              </p>
            </div>
            <span className="aa-ops-pill amber">Mismatch</span>
          </div>
          <div className="aa-ops-risk-bars">
            {mismatchBars.map((row) => (
              <div key={row.block} className="aa-ops-risk-row">
                <div className="aa-ops-risk-name">{row.block}</div>
                <div className="aa-ops-risk-track">
                  <div
                    className={`aa-ops-risk-fill ${row.tone}`}
                    style={{ width: `${row.width}%` }}
                  />
                </div>
                <div className="aa-ops-risk-score">{pct(row.score)}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="aa-ops-panel">
          <div className="aa-ops-panel-head">
            <div>
              <h3>MBU Pending by Age Group</h3>
              <p>
                Age-wise backlog view helps route cases to school and block teams with the highest
                pending load.
              </p>
            </div>
            <span className="aa-ops-pill">Age-wise</span>
          </div>
          <div className="aa-ops-age-bars">
            {mbuBars.map((row) => {
              const total = row.total || 1;
              const pct515 = (row.mbu515 / total) * 100;
              const pct15 = (row.mbu15plus / total) * 100;
              return (
                <div key={row.block} className="aa-ops-age-row">
                  <div className="aa-ops-age-label">{row.block}</div>
                  <div className="aa-ops-age-track">
                    <div className="aa-ops-age-fill" style={{ width: `${pct515}%` }} />
                    {pct15 > 0 ? (
                      <div className="aa-ops-age-fill plus" style={{ width: `${pct15}%` }} />
                    ) : null}
                  </div>
                  <div className="aa-ops-age-value">{row.label}</div>
                </div>
              );
            })}
          </div>
          <div className="aa-ops-legend-row">
            <span>
              <i className="aa-ops-legend-dot" style={{ background: "#06b6d4" }} />
              Age 15+
            </span>
            <span>
              <i className="aa-ops-legend-dot" style={{ background: "var(--aa-purple)" }} />
              Age 5–15
            </span>
          </div>
        </article>
      </section>

      <div className="aa-ops-section-title" id="aa-ops-schools">
        <div>
          <h2>High-Risk Schools Needing Follow-Up</h2>
          <p>
            The old long table is converted into a control register with risk level, accountable
            owner and next action. This makes the tab operational, not just analytical.
          </p>
        </div>
        <button type="button" className="aa-ops-section-action" onClick={() => scrollToId("aa-ops-control")}>
          Back to top ↑
        </button>
      </div>

      <section className="aa-ops-panel aa-ops-table-card">
        <div className="aa-ops-table-head">
          <div>
            <h3>Top 20 High-Risk Schools by Exception Rate</h3>
            <p>Prioritized for block-level validation, school visit and Aadhaar correction drive.</p>
          </div>
          <span className="aa-ops-pill red">{highRiskCount} Critical</span>
        </div>
        <div className="aa-ops-table-wrap">
          <table className="aa-ops-table" aria-label="Top high-risk schools by exception rate">
            <thead>
              <tr>
                <th>Rank</th>
                <th>School Name</th>
                <th>Block</th>
                <th>Enrolment</th>
                <th>Exception Rate</th>
                <th>Coverage</th>
                <th>Risk</th>
                <th>Owner</th>
                <th>Next Action</th>
              </tr>
            </thead>
            <tbody>
              {schoolRows.map((row) => (
                <tr key={`${row.udiseCode || row.school}-${row.rank}`}>
                  <td>
                    <span className="aa-ops-rank">{row.rank}</span>
                  </td>
                  <td>
                    {row.udiseCode ? (
                      <SchoolLink udiseCode={row.udiseCode} className="aa-ops-school-link">
                        {row.school}
                      </SchoolLink>
                    ) : (
                      <span className="aa-ops-school-link">{row.school}</span>
                    )}
                  </td>
                  <td>
                    {row.blockCode ? (
                      <BlockLink blockCode={row.blockCode}>{row.block}</BlockLink>
                    ) : (
                      row.block
                    )}
                  </td>
                  <td>{row.enrolment.toLocaleString("en-IN")}</td>
                  <td>
                    <span className={`aa-ops-tag ${row.exceptionTone}`}>{row.exceptionRate}</span>
                  </td>
                  <td>{row.coverage}</td>
                  <td>
                    <span className={`aa-ops-tag ${row.riskTone}`}>{row.risk}</span>
                  </td>
                  <td className="aa-ops-owner">{row.owner}</td>
                  <td>
                    <span className="aa-ops-action-link">{row.nextAction} →</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="aa-ops-section-title">
        <div>
          <h2>Action and Accountability Model</h2>
          <p>
            Operational control should end with a clear action mechanism: owner, timeline and
            measurable closure indicator.
          </p>
        </div>
        <button
          type="button"
          className="aa-ops-section-action"
          onClick={() => scrollToId("aa-ops-diagnostics")}
        >
          Review diagnostics →
        </button>
      </div>

      <section className="aa-ops-accountability-grid">
        {accountabilityCards.map((card) => {
          const Icon = ACCOUNTABILITY_ICONS[card.iconClass] || AlertTriangle;
          return (
            <article key={card.title} className="aa-ops-panel">
              <div className="aa-ops-panel-head">
                <div>
                  <h3>{toTitleCase(card.title)}</h3>
                  <p>{card.subtitle}</p>
                </div>
                <span className={`aa-ops-pill ${card.pillTone || ""}`.trim()}>{card.pill}</span>
              </div>
              <div className="aa-ops-action-box">
                <div className="aa-ops-action-item">
                  <div className={`aa-ops-icon-box ${card.iconClass}`}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h4>{card.actionTitle}</h4>
                    <p>{card.actionBody}</p>
                  </div>
                </div>
                <div className="aa-ops-action-footer">
                  <b>{card.successTitle}</b>
                  <span>{card.successBody}</span>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      <AadhaarKpiSchoolsModal
        open={Boolean(kpiMetricModal)}
        metric={kpiMetricModal}
        onClose={() => setKpiMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={
          kpiMetricModal === "open_aadhaar_issues"
            ? overview?.aadhaar_exception_pct
            : kpiMetricModal === "failed_records"
              ? overview?.aadhaar_failed_pct
              : kpiMetricModal === "name_mismatch"
                ? overview?.name_mismatch_pct
                : kpiMetricModal === "mbu_pending"
                  ? overview?.mbu_pending_total_pct
                  : kpiMetricModal === "high_risk_schools"
                    ? overview?.aadhaar_exception_pct
                    : null
        }
        districtAggregate={
          kpiMetricModal === "open_aadhaar_issues"
            ? openIssues
            : kpiMetricModal === "failed_records"
              ? overview?.aadhaar_failed
              : kpiMetricModal === "name_mismatch"
                ? Math.max(0, (overview?.aadhaar_passed ?? 0) - (overview?.name_match ?? 0))
                : kpiMetricModal === "mbu_pending"
                  ? overview?.mbu_pending_total
                  : kpiMetricModal === "high_risk_schools"
                    ? highRiskCount
                    : null
        }
      />
    </div>
  );
}
